/**
 * Step Indicator Component - Phase 4 Implementation
 *
 * Visual indicator showing current step progress and layer information.
 */

import React from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  Person as StarsIcon,
  Palette as LooksIcon,
  Public as WorldsIcon,
  DirectionsRun as MovesIcon,
  AudioFile as SongsIcon,
  LibraryBooks as CompositeIcon,
  Storage as TrainingIcon,
  Gavel as RightsIcon,
  PersonalVideo as PersonalizeIcon,
  Business as BrandedIcon,
} from '@mui/icons-material';
import { LayerType } from '../../../types/layerMetadata';

export interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
  stepName: string;
  stepDescription: string;
  progress: number;
  selectedLayer: LayerType | null;
}

const LAYER_ICONS: Record<LayerType, React.ElementType> = {
  S: StarsIcon,
  L: LooksIcon,
  W: WorldsIcon,
  M: MovesIcon,
  G: SongsIcon,
  C: CompositeIcon,
  T: TrainingIcon,
  R: RightsIcon,
  P: PersonalizeIcon,
  B: BrandedIcon,
};

const LAYER_NAMES: Record<LayerType, string> = {
  S: 'Stars',
  L: 'Looks',
  W: 'Worlds',
  M: 'Moves',
  G: 'Songs',
  C: 'Composite',
  T: 'Training Data',
  R: 'Rights',
  P: 'Personalize',
  B: 'Branded',
};

const LAYER_COLORS: Record<LayerType, string> = {
  G: '#1976d2', // Blue - Songs (matches layerConfig)
  S: '#e91e63', // Pink - Stars (matches layerConfig)
  L: '#9c27b0', // Purple - Looks (matches layerConfig)
  M: '#ff9800', // Orange - Moves (matches layerConfig)
  W: '#4caf50', // Green - Worlds (matches layerConfig)
  B: '#f44336', // Red - Branded (matches layerConfig)
  P: '#00bcd4', // Cyan - Personalize (matches layerConfig)
  T: '#673ab7', // Deep Purple - Training Data (matches layerConfig)
  C: '#795548', // Brown - Composite (matches layerConfig)
  R: '#607d8b', // Blue Grey - Rights (matches layerConfig)
};

export const StepIndicator: React.FC<StepIndicatorProps> = ({
  currentStep,
  totalSteps,
  stepName,
  stepDescription,
  progress,
  selectedLayer,
}) => {
  const getStepNames = (layer: LayerType | null): string[] => {
    // Composite layer: 5 steps with Component Selection before File Upload
    if (layer === 'C') {
      return [
        'Layer Selection',
        'Taxonomy Selection',
        'Component Selection', // One Song, Star, Look, Move, World
        'File Upload & Asset Type',
        'Review & Submit',
      ];
    }

    // Other complex layers (T, R, P) may use AI Processing step
    if (['T', 'R', 'P'].includes(layer || '')) {
      return [
        'Layer Selection',
        'Taxonomy Selection',
        'File Upload & Asset Type',
        'AI Processing', // AI generation for complex layers
        'Review & Submit',
      ];
    }

    // Standard 4-step workflow for basic layers (G, S, L, M, W, B)
    return [
      'Layer Selection',
      'Taxonomy Selection',
      'File Upload & Asset Type', // Combines asset type selection, file upload, and AI generation
      'Review & Submit', // Display metadata, edit, and submit
    ];
  };

  const stepNames = getStepNames(selectedLayer);
  const LayerIcon = selectedLayer ? LAYER_ICONS[selectedLayer] : null;
  const layerColor = selectedLayer ? LAYER_COLORS[selectedLayer] : '#666';

  return (
    <Box
      sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        background: selectedLayer
          ? `linear-gradient(135deg, ${layerColor}10 0%, ${layerColor}05 100%)`
          : 'background.default',
      }}
    >
      {/* Header with layer info - only show if layer is selected */}
      {selectedLayer && LayerIcon && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LayerIcon
            sx={{
              fontSize: 28,
              color: layerColor,
              mr: 1.5,
            }}
          />
          <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>
            {LAYER_NAMES[selectedLayer]} Asset Registration
          </Typography>
          <Chip
            label={selectedLayer}
            size="small"
            sx={{
              bgcolor: layerColor,
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        </Box>
      )}

      {/* Current step info - more compact */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            Step {currentStep + 1}: {stepName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {currentStep + 1} of {totalSteps}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          {stepDescription}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              bgcolor: layerColor,
              borderRadius: 3,
            },
          }}
        />
      </Box>

      {/* Step stepper */}
      <Stepper activeStep={currentStep} alternativeLabel>
        {stepNames.map((name, index) => (
          <Step key={name}>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '0.75rem',
                  fontWeight: index === currentStep ? 'bold' : 'normal',
                },
                '& .MuiStepIcon-root': {
                  color: index <= currentStep ? layerColor : 'grey.300',
                  '&.Mui-active': {
                    color: layerColor,
                  },
                  '&.Mui-completed': {
                    color: layerColor,
                  },
                },
              }}
            >
              {name}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default StepIndicator;
